import React from 'react';
import { X, Shield, ShieldCheck, Check } from 'lucide-react';

interface RolePermissionsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    roleName: string;
    permissions: Record<string, string> | string | null | undefined;
}

const RolePermissionsSidebar: React.FC<RolePermissionsSidebarProps> = ({ isOpen, onClose, roleName, permissions }) => {

    const convertStringToOperations = (permissionStr: string): string[] => {
        const reverseMap: Record<string, string> = {
            'c': 'create',
            'r': 'read',
            'u': 'update',
            'd': 'delete'
        };

        return permissionStr.split('').map(char => reverseMap[char]).filter(Boolean);
    };

    let parsedPermissions: Record<string, string> | null = null;
    try {
        if (typeof permissions === "string") {
            parsedPermissions = JSON.parse(permissions);
        } else if (typeof permissions === "object" && permissions !== null) {
            parsedPermissions = permissions;
        }
    } catch {
        parsedPermissions = null;
    }

    const operations = ["create", "read", "update", "delete"];

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-slate-900/10 backdrop-blur-sm transition-opacity duration-300 z-[60] ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />

            {/* Sidebar */}
            <div className={`fixed top-0 right-0 h-full w-full sm:w-[450px] bg-white shadow-2xl border-l border-slate-200 transition-transform duration-500 ease-in-out transform z-[70] ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-orange-50 border border-orange-100 rounded-xl shadow-sm">
                                <ShieldCheck className="text-orange-600" size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-black text-slate-800 leading-tight tracking-tight">{roleName}</h2>
                                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">Role Permissions</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all duration-300"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-white">
                        {!parsedPermissions || Object.keys(parsedPermissions).length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <div className="p-4 bg-slate-50 rounded-full mb-4">
                                    <Shield className="text-slate-200" size={40} />
                                </div>
                                <h3 className="text-slate-800 font-black text-sm uppercase tracking-tight">No permissions assigned</h3>
                                <p className="text-slate-400 text-[11px] mt-2 font-bold max-w-[200px]">This role doesn't have any specific action permissions yet.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {Object.entries(parsedPermissions).map(([category, operationStr]) => {
                                    const currentOps = convertStringToOperations(operationStr);

                                    return (
                                        <div key={category} className="bg-slate-50/30 border-2 border-slate-50 rounded-2xl p-4 hover:border-slate-100 hover:bg-slate-50/50 transition-all duration-300 group">
                                            <div className="flex items-center justify-between mb-4 gap-3">
                                                <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest leading-tight">
                                                    {category.replace(/_/g, " ")}
                                                </h3>
                                                <div className="px-2 py-0.5 bg-white border border-slate-100 rounded-lg text-[10px] font-black text-orange-600 shadow-sm transition-all group-hover:border-orange-100">
                                                    {currentOps.length} OPS
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                {operations.map((op) => {
                                                    const hasOp = currentOps.includes(op);
                                                    return (
                                                        <div
                                                            key={op}
                                                            className={`flex items-center gap-2 px-2.5 py-2.5 rounded-xl border transition-all duration-300 ${hasOp
                                                                ? 'bg-white border-slate-200 text-slate-700 shadow-sm font-bold'
                                                                : 'bg-transparent border-slate-50 text-slate-300 opacity-50'
                                                                }`}
                                                            title={op}
                                                        >
                                                            <div className={`w-4 h-4 rounded-md flex items-center justify-center border transition-all ${hasOp
                                                                ? 'bg-orange-600 border-orange-500'
                                                                : 'border-slate-200 bg-white'
                                                                }`}>
                                                                {hasOp && <Check size={10} className="text-white stroke-[4]" />}
                                                            </div>
                                                            <span className="text-[10px] font-black uppercase tracking-wider">
                                                                {op}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default RolePermissionsSidebar;
